import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";

import ClotureIcon from "src/core/assets/images/icons/cloture.svg?react";
import DeleteIcon from "src/core/assets/images/icons/delete.svg?react";
import EditIcon from "src/core/assets/images/icons/edit.svg?react";
import ReloadIcon from "src/core/assets/images/icons/reload.svg?react";
import { Button } from "src/core/components/ui/button";
import { RISK_LEVEL_CLASSES } from "src/core/constants/risk";
import { IMission } from "src/core/hooks/folder/useFolderMissions";
import { cn } from "src/core/lib/utils";

type MissionsColumnsProps = {
  isActive: boolean;
  t: (key: string) => string;
  renewMission: (mission: IMission) => void;
  deleteMission: (mission: IMission) => void;
  editMission: (mission: IMission) => void;
  closeMission: (mission: IMission) => void;
  isReadOnly?: boolean;
  folderFileCreation: string | null;
};

export const missionsColumns = ({
  isActive,
  t,
  renewMission,
  deleteMission,
  editMission,
  isReadOnly = false,
  closeMission,
  folderFileCreation,
}: MissionsColumnsProps): ColumnDef<IMission>[] => [
  {
    accessorKey: "missionType",
    header: isActive
      ? t("folder.missions.columns.activeMissions")
      : t("folder.missions.columns.inactiveMissions"),
    enableSorting: false,
    size: 200, // w-2/5 équivaut à 40% de la largeur
    cell: ({ row }) => {
      const className = isActive ? "" : "text-muted";
      return (
        <div className="flex flex-row items-center gap-x-10">
          <span
            className={`w-[16px] h-[16px] rounded-full ${RISK_LEVEL_CLASSES[row.original?.risk]}`}
          ></span>
          <span className={className}>{row.original?.missionType || "-"}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "renewDate",
    header: t("folder.missions.columns.renewDate"),
    enableSorting: false,
    size: 100, // w-1/5 équivaut à 20% de la largeur
    cell: ({ row }) => {
      const className = isActive ? "" : "text-muted";
      const date = row.original?.renewDate;
      return <span className={className}>{date ? format(new Date(date), "dd/MM/yyyy") : "-"}</span>;
    },
  },
  {
    accessorKey: "actions",
    header: "",
    enableSorting: false,
    size: 200, // w-2/5 équivaut à 40% de la largeur
    cell: ({ row }) => {
      const className = isActive ? "" : "text-muted";
      return (
        <div className={cn("flex flex-row items-center justify-end gap-x-30 mr-10", className)}>
          {!isReadOnly && folderFileCreation && (
            <>
              {isActive ? (
                row.original?.creation &&
                new Date(row.original?.creation) > new Date(folderFileCreation) ? (
                  <>
                    <Button variant="action" size="icon" onClick={() => editMission(row.original)}>
                      <EditIcon />
                    </Button>
                    <Button
                      variant="action"
                      size="icon"
                      onClick={() => deleteMission(row.original)}
                    >
                      <DeleteIcon />
                    </Button>
                  </>
                ) : row.original?.isClosable ? (
                  <Button
                    variant="actionWithText"
                    size="none"
                    onClick={() => closeMission(row.original)}
                  >
                    <ClotureIcon />
                    {t("folder.missions.columns.close")}
                  </Button>
                ) : null
              ) : (
                <Button variant="default" size="small" onClick={() => renewMission(row.original)}>
                  <ReloadIcon />
                  {t("folder.missions.columns.renew")}
                </Button>
              )}
            </>
          )}
        </div>
      );
    },
  },
];
